import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../../utils/dataUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { useTheme } from '../../hooks/useTheme';
import {
  ChartEmptyState,
  getChartTheme,
  tooltipStyle,
  barValueAxis,
  categoryAxis,
  barDataset,
  barChartHeight,
  barValueLabels,
  BAR_LABEL_PADDING
} from './chartTheme';

const MAX_PRODUCTS = 15;

export const ProductChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const currencyKey = useCurrency();
  const themeName = useTheme();
  const keyTheme = getChartTheme(themeName);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Return early if no meaningful data
    if (!data || Object.keys(data).length === 0) {
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    const theme = getChartTheme(themeName);

    // Sort products by spend amount (descending)
    const sortedEntries = Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_PRODUCTS); // Limit to the top products for better readability

    const labels = sortedEntries.map(([key]) => key);
    const values = sortedEntries.map(([, value]) => value);

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        // Discounts (negative amounts) get their own colour
        datasets: [barDataset(
          'Product Spend',
          values,
          values.map(value => value < 0 ? theme.discount : theme.bar)
        )]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        layout: { padding: BAR_LABEL_PADDING },
        scales: {
          x: barValueAxis(theme),
          y: categoryAxis(30, theme)
        },
        plugins: {
          tooltip: {
            ...tooltipStyle(theme),
            callbacks: {
              label: context => `${context.raw < 0 ? 'Discount' : 'Spend'}: ${formatCurrency(context.raw)}`
            }
          },
          legend: {
            display: false // Hide legend since product names are on Y-axis
          },
          title: {
            display: Object.keys(data).length > MAX_PRODUCTS,
            text: `Showing top ${MAX_PRODUCTS} of ${Object.keys(data).length} products`,
            position: 'bottom',
            padding: {
              top: 10
            },
            color: theme.ink.muted,
            font: {
              size: 12,
              weight: 'normal'
            }
          }
        }
      },
      plugins: [barValueLabels(theme)]
    });

    return () => {
      if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
        chartInstance.current.destroy();
      }
    };
  }, [data, currencyKey, themeName]);

  // Determine if we have data or need to show a message
  const hasData = data && Object.keys(data).length > 0;
  const barCount = hasData ? Math.min(Object.keys(data).length, MAX_PRODUCTS) : 0;
  const hasDiscounts = hasData && Object.values(data).some(value => value < 0);

  return (
    <div>
      <div style={{ height: hasData ? `${barChartHeight(barCount)}px` : '100%', position: 'relative' }}>
        {!hasData ? (
          <ChartEmptyState>No product data available</ChartEmptyState>
        ) : (
          <canvas ref={chartRef} role="img" aria-label="Bar chart of spend by product" />
        )}
      </div>
      {hasDiscounts && (
        <ul className="chart-key">
          <li><span className="legend-swatch" style={{ backgroundColor: keyTheme.bar }} aria-hidden="true"></span>Spend</li>
          <li><span className="legend-swatch" style={{ backgroundColor: keyTheme.discount }} aria-hidden="true"></span>Discount</li>
        </ul>
      )}
    </div>
  );
};
